<script setup lang="ts">
defineOptions({
  inheritAttrs: false,
})

const DEFAULT_PLACEHOLDER = 30

const {
  aspectRatio = '',
  cover = true,
  fit,
  loading = 'lazy',
  noPlaceholder = false,
  preload = false,
  sizes = '',
} = defineProps<{
  aspectRatio?: string
  cover?: boolean
  fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside'
  loading?: 'eager' | 'lazy'
  noPlaceholder?: boolean
  preload?: boolean
  sizes?: string
}>()
const attrs = useAttrs() as {
  width: number
  height: number
  src: string
  format: string
}

const $img = useImage()
const mainImageRef = useTemplateRef<HTMLImageElement>('mainImageRef')
const placeholderRef = useTemplateRef<HTMLImageElement>('placeholderRef')

const className = computed(() =>
  cover ? 'size-full object-cover' : 'mx-auto block object-contain',
)
const format = computed(() => {
  if (!attrs.src) return
  // get the path section of the src, excluding query params
  const [pathname] = attrs.src.split('?')
  // Exclude SVG, GIF from automatic formatting to WEBP
  if (pathname.endsWith('svg') || pathname.endsWith('gif')) return attrs.format
  return 'webp'
})
const sizesComputed = computed(() =>
  (attrs.width && attrs.height) || (!attrs.width && attrs.height)
    ? undefined
    : // https://github.com/nuxt/image/issues/1433
      sizes.replace(/^100vw?/, '').trim() ||
      'sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw',
)

const placeholderLoaded = ref(noPlaceholder)
const placeholder = computed(() => {
  if (!attrs.src || noPlaceholder || placeholderLoaded.value) return null

  let ratio = 1
  if (attrs.height && attrs.width) {
    ratio = attrs.width / attrs.height
  } else if (aspectRatio) {
    const [w = 1, h = 1] = aspectRatio.split('/').map(Number)
    if (w && h) ratio = w / h
  }
  const width = Math.round(
    Math.max(
      (attrs.width ?? 0) / DEFAULT_PLACEHOLDER,
      DEFAULT_PLACEHOLDER * ratio,
    ),
  )
  const height = Math.round(width * (1 / ratio))

  const img = $img.getImage(src.value, {
    densities: 'x1',
    modifiers: { blur: 5 },
  })
  const url = new URL(img.url)
  url.searchParams.append('height', height.toString())
  url.searchParams.append('width', width.toString())
  return { height, width, src: url.toString() }
})

const src = computed(
  () => attrs.src || 'production/assets/images/placeholder-badge.svg',
)
const onPlaceholderLoaded = () => {
  placeholderLoaded.value = true
}

onMounted(() => {
  /**
   *  If the main image or the placeholder is already loaded during client-side rendering,
   * Then we safely can load the main image and drop the placeholder out
   *  */
  if (
    !placeholderLoaded.value &&
    (mainImageRef.value?.complete || placeholderRef.value?.complete)
  )
    placeholderLoaded.value = true
})
</script>

<template>
  <NuxtImg
    v-if="$attrs.src && placeholderLoaded"
    ref="mainImageRef"
    :class="className"
    v-bind="$attrs"
    densities="x1 x2"
    :fit="fit"
    :format="format"
    :loading="loading"
    :src="src"
    :sizes="sizesComputed"
    :preload="preload"
    @load="onPlaceholderLoaded"
  />
  <img
    v-else-if="placeholder"
    ref="placeholderRef"
    :class="[className, 'animate-pulse']"
    v-bind="$attrs"
    :format="format"
    :height="placeholder.height ?? DEFAULT_PLACEHOLDER"
    :src="placeholder.src"
    :width="placeholder.width ?? DEFAULT_PLACEHOLDER"
    @load="onPlaceholderLoaded"
    @error="onPlaceholderLoaded"
  />
  <span v-else class="empty-placeholder">
    <NuxtImg
      v-bind="$attrs"
      densities="x1 x2"
      format="webp"
      height="100"
      loading="lazy"
      :sizes="sizesComputed"
      :src="src"
      width="100"
    />
  </span>
</template>

<style scoped>
.empty-placeholder {
  @apply flex aspect-square h-full items-center justify-center bg-gray-100 p-2;

  img {
    @apply max-h-[100px];
  }
}
</style>
